// src/components/resources/tools/generators/ReviewMockup/AppStoreReview.tsx
'use client'

import { Star, UserRound, Share2, ChevronRight } from 'lucide-react'
import { clsx } from 'clsx'
import Image from 'next/image'
import { AppStoreReviewCard } from './AppStoreReviewCard'
import { appStoreMaxBar, buildAppStoreReviews } from './appstore-reviews'
import type { ReviewConfig } from './types'

/**
 * App Store review mockup component — renders the App Store product page the
 * marketing hero image advertises: the app-header row (icon, name, tagline,
 * GET), the 4-stat summary strip (ratings/age/chart/developer), the aggregate
 * rating summary with a 5★→1★ distribution histogram, and a stacked
 * "Ratings & Reviews" list (primary review + additional reviews) followed by
 * the developer response.
 */
export function AppStoreReview({ config }: { config: ReviewConfig }): JSX.Element {
  const app = config.appStore
  const maxBar = appStoreMaxBar(app.ratingDistribution)
  const reviews = buildAppStoreReviews(config)

  return (
    <div className="mx-auto w-full max-w-2xl overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm">
      {/* App header row */}
      <div className="flex items-center gap-4 p-5">
        <div className="flex h-20 w-20 flex-shrink-0 items-center justify-center overflow-hidden rounded-[18px] bg-gradient-to-br from-indigo-500 to-purple-600 shadow-inner">
          {app.icon ? (
            <Image
              src={app.icon}
              alt={`${app.appName} icon`}
              className="h-full w-full object-cover"
              width={80}
              height={80}
              unoptimized
              priority={false}
            />
          ) : (
            <span className="text-3xl font-bold text-white">{app.appName.charAt(0) || 'A'}</span>
          )}
        </div>
        <div className="min-w-0 flex-1">
          <h3 className="truncate text-xl font-semibold text-gray-900">{app.appName}</h3>
          <p className="truncate text-sm text-gray-500">{app.tagline}</p>
          <div className="mt-2 flex items-center gap-3">
            <button
              className="rounded-full bg-blue-600 px-6 py-1 text-sm font-semibold text-white"
              aria-label={`Get ${app.appName}`}
            >
              GET
            </button>
            <Share2 className="h-4 w-4 text-blue-600" aria-hidden="true" />
          </div>
        </div>
      </div>

      {/* 4-stat summary strip */}
      <div className="grid grid-cols-4 divide-x divide-gray-200 border-y border-gray-200 text-center">
        <div className="px-2 py-3">
          <div className="text-[11px] font-medium uppercase tracking-wide text-gray-400">
            {app.ratingCount} Ratings
          </div>
          <div className="mt-1 text-lg font-bold text-gray-900">{app.ratingAverage.toFixed(1)}</div>
          <div className="mt-0.5 flex items-center justify-center gap-0.5" aria-hidden="true">
            {Array.from({ length: 5 }, (_, i) => (
              <Star
                key={i}
                className={clsx(
                  'h-2.5 w-2.5',
                  i < Math.round(app.ratingAverage)
                    ? 'fill-gray-500 text-gray-500'
                    : 'fill-gray-300 text-gray-300'
                )}
              />
            ))}
          </div>
        </div>
        <div className="px-2 py-3">
          <div className="text-[11px] font-medium uppercase tracking-wide text-gray-400">Age</div>
          <div className="mt-1 text-lg font-bold text-gray-900">{app.ageRating}</div>
          <div className="mt-0.5 text-[11px] text-gray-400">Years Old</div>
        </div>
        <div className="px-2 py-3">
          <div className="text-[11px] font-medium uppercase tracking-wide text-gray-400">Chart</div>
          <div className="mt-1 text-lg font-bold text-gray-900">{app.chartRank}</div>
          <div className="mt-0.5 truncate text-[11px] text-gray-400">{app.chartCategory}</div>
        </div>
        <div className="px-2 py-3">
          <div className="text-[11px] font-medium uppercase tracking-wide text-gray-400">Developer</div>
          <div className="mt-1 flex justify-center">
            <UserRound className="h-5 w-5 text-gray-500" aria-hidden="true" />
          </div>
          <div className="mt-0.5 truncate text-[11px] text-gray-400">{config.businessName}</div>
        </div>
      </div>

      {/* Ratings & Reviews heading */}
      <div className="flex items-center justify-between px-5 pt-4">
        <h3 className="text-lg font-bold text-gray-900">Ratings &amp; Reviews</h3>
        <button className="flex items-center gap-0.5 text-sm text-blue-600" aria-label="See all reviews">
          See All
          <ChevronRight className="h-4 w-4" aria-hidden="true" />
        </button>
      </div>

      {/* Aggregate summary: big average + distribution histogram */}
      <div className="flex items-end gap-6 px-5 py-3">
        <div className="flex-shrink-0">
          <div className="text-5xl font-bold leading-none text-gray-900">
            {app.ratingAverage.toFixed(1)}
          </div>
          <div className="mt-1 text-xs text-gray-400">out of 5</div>
        </div>
        <div className="flex-1">
          {app.ratingDistribution.map((weight, i) => {
            const starValue = 5 - i
            return (
              <div key={starValue} className="flex items-center gap-1" aria-hidden="true">
                <span className="flex w-10 flex-shrink-0 items-center justify-end gap-px text-gray-400">
                  {Array.from({ length: starValue }, (_, s) => (
                    <Star key={s} className="h-2 w-2 fill-gray-400 text-gray-400" />
                  ))}
                </span>
                <span className="h-1.5 flex-1 overflow-hidden rounded-full bg-gray-200">
                  <span
                    className="block h-full rounded-full bg-gray-500"
                    style={{ width: `${(weight / maxBar) * 100}%` }}
                  />
                </span>
              </div>
            )
          })}
          <div className="mt-1 text-right text-xs text-gray-400">{app.ratingCount} Ratings</div>
        </div>
      </div>

      {/* Stacked review list */}
      <div className="divide-y divide-gray-100 px-5 pb-2">
        {reviews.map((review, i) => (
          <AppStoreReviewCard key={i} {...review} />
        ))}
      </div>

      {/* Developer Response (to the primary review) */}
      <div className="bg-gray-50 px-5 py-4">
        <div className="text-xs font-medium text-gray-500">Developer Response</div>
        <p className="mt-2 text-sm leading-relaxed text-gray-900">
          We appreciate your feedback! It&apos;s wonderful to know our platform is making a real difference in your workflow.
        </p>
        <div className="mt-2 flex items-center gap-2">
          <span className="text-xs text-gray-500">{config.businessName}</span>
          <span className="text-gray-400">&middot;</span>
          <span className="text-xs text-gray-500">2 days ago</span>
        </div>
      </div>
    </div>
  )
}
